import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { hooks } from 'farmx-redux-core';
import { Typography, Tag } from 'antd';
import EditableTable from "./EditableTable";
import { isEqual } from "lodash";
import {
  getNodeStatusData, getSensorDetailURLPath,
  getSensorName, getSensorStatus, getColorForId
} from "helper/common";

const { useSensorsForEntity } = hooks;

export default function NodeAssociatedSensorsList() {
  const [sensorList, setSensorList] = useState<any>([]);
  const [sensorInfo, setSensorInfo] = useState<any>(null);
  const [sensorStatusList, setSensorStatusList] = useState<any>([]);
  const { t } = useTranslation();
  const data = sessionStorage.getItem("associatedSensorsData");
  const {
    entity, ranch, block,
    identifier: nodeIdentifier, name: nodeName, type: nodeType, sub_type
  } = sensorInfo || {};
  const sensorsForEntity = useSensorsForEntity(entity);

  useEffect(() => {
    let selectorType = 'entity';
    let id = entity;
    if (ranch && !block) {
      selectorType = 'ranch';
      id = ranch;
    }
    if (block) {
      selectorType = 'block';
      id = block;
    }
  
    if (!id) return;

    getNodeStatusData(selectorType, id, '', (response, success) => {
      if (success && !isEqual(sensorStatusList, response)) {        
        setSensorStatusList(response);
      }
    })
  }, [entity, ranch, block])

  useEffect(() => {
    let parsedData: any = {};
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (e) {
      console.error("Invalid JSON in sessionStorage:", e);
    }

    if (parsedData?.associatedSensors) {
      const updatedData = parsedData?.associatedSensors
        ?.map((item) => {
          const { type, identifier } = item || {};
          return {
            key: `${type}/${identifier}`,
            sensorName: getSensorName(sensorsForEntity, { type, identifier }),
            status: t(getSensorStatus(type, identifier, sensorStatusList)),
            ...item,
          }
        });
      setSensorInfo(parsedData);
      setSensorList(updatedData);
    }
  }, [data, sensorStatusList]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "index",
      key: "index",
      render: (_: any, __: any, index: number) => index + 1,
      width: 50,
    },{
      title: t('Sensor Name'),
      dataIndex: 'sensorName',
      key: 'sensorName',
      width: 100,
      render: (_, record) => {
        const { type, identifier } = record || {};
        const [sensorStatus] = sensorStatusList?.filter((item) => (type === item?.type
          && identifier === item?.identifier))
        const { color, key, tooltipText } = getColorForId(sensorStatus);
        return <Tag title={t(tooltipText)} color={color} key={key}>
          {getSensorName(sensorsForEntity, record)}
        </Tag>;
      },
    },{
      title: t('Identifier'),
      dataIndex: 'identifier',
      key: 'identifier',
      width: 100
    },{
      title: t('Sensor Type'),
      dataIndex: 'type',
      key: 'type',
      width: 100
    },{
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100
    }
  ]

  // To open sensor details page in new tab
  function onRowClick(record) {
    const { type, identifier } = record || {};
    const sensorPath = getSensorDetailURLPath(type);
    const url =  `/list/${sensorPath}/${type}/${identifier}`;
    window.open(url, "_blank")
  }

  return (
    <div className="single-column-page padded desktop-container desktop-container-config">
      <div className="flex-space-between align-baseline">
        <div className="custom-page-header">
          <Typography.Title level={3}>{t('Associated Sensors')}</Typography.Title>
        </div>
      </div>
      <div className="flex-space-between">
        <div>
          <span className="font-bold">{`${t('Node')}: `}</span>
          <span>{nodeName || t('No Name')}</span>
        </div>
        <div>
          <span className="font-bold">{`${t('Identifier')}: `}</span>
          <span>{nodeIdentifier}</span>
        </div>
        <div>
          <span className="font-bold">{`${t('Type')}: `}</span>
          <span>{nodeType}</span>
        </div>
        <div>
          <span className="font-bold">{`${t('Sub Type')}: `}</span>
          <span>{sub_type}</span>
        </div>
      </div>
      <EditableTable
        data={sensorList || []}
        columns={columns}
        showIsActive={false}
        showAction={false}
        onRow={(record) => ({
          onClick: () => onRowClick(record),
          style: { cursor: 'pointer' },
        })}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "" : "table-row-dark"
        }
      />
    </div>
  );
}
